import { useState } from 'react'

import { createGlobalStyle } from 'styled-components'

import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage, Modal } from '@b-stock/fe-core/bstock-react'

import { Fullscreen } from '@/components/common/modal/Fullscreen'

import { useManifestTableTracking } from '../../hooks/useManifestTableTracking'
import { ManifestSearchBar } from './controls/ManifestSearchBar'
import type { ActiveSort } from './FooterRowCount'
import { FooterRowCount } from './FooterRowCount'
import {
  ManifestProvider,
  getVisibleStartEnd,
  useManifestContext,
} from './ManifestContext'
import { ManifestGrid } from './ManifestGrid'
import { useManifestFullscreenTracking } from './tracking/useManifestFullscreenTracking'
import { useManifestScrollDepthTracking } from './tracking/useManifestScrollDepthTracking'
import { FULLSCREEN_MANIFEST_LIMIT } from './useManifestDatasource'

// The live-chat widget (#livesdk__campaign) sits at z-index 9990 and would
// otherwise paint over the modal. Boost fe-core's portal wrapper above it for
// the lifetime of this modal.
const RaisePortalAboveLivesdk = createGlobalStyle`
  #modal-portal-wrapper {
    position: relative;
    z-index: 9999;
  }
`

export const ManifestFullscreenModal = ({
  listing,
  onClose,
}: {
  listing: OrchestratedListingDetails
  onClose: () => void
}) => (
  <>
    <RaisePortalAboveLivesdk />
    <Modal appearance="fullscreen" closeModal={onClose}>
      <ManifestProvider limit={FULLSCREEN_MANIFEST_LIMIT} listing={listing}>
        <ManifestFullscreenContent listing={listing} onClose={onClose} />
      </ManifestProvider>
    </Modal>
  </>
)

const ManifestFullscreenContent = ({
  listing,
  onClose,
}: {
  listing: OrchestratedListingDetails
  onClose: () => void
}) => {
  const { columnDefs, limit, total, visibleRange } = useManifestContext()
  const {
    containerRef,
    agGridProps: agGridTrackingProps,
    onContainerClick,
  } = useManifestFullscreenTracking(listing)
  const { onManifestFullyViewed, onManifestScrollDepth } =
    useManifestTableTracking(listing)

  const [activeSort, setActiveSort] = useState<ActiveSort | null>(null)
  const [, setPage] = useState(1)
  const { end, start } = getVisibleStartEnd(visibleRange, limit, total)

  // [BP-2378] Fullscreen is always infinite-scroll (unconditional, unlike the
  // inline grid), so this is unconditionally enabled here.
  useManifestScrollDepthTracking({
    enabled: true,
    onFullyViewed: onManifestFullyViewed,
    onScrollDepth: onManifestScrollDepth,
    surface: 'fullscreen',
    total,
    visibleRange,
  })

  return (
    <>
      <Fullscreen.Header>
        <Fullscreen.Title>
          <FormattedMessage id="Listing.manifestAgGrid.title" />
        </Fullscreen.Title>
        <Fullscreen.ExitButton onClick={onClose} />
      </Fullscreen.Header>
      <Fullscreen.Body ref={containerRef} onClick={onContainerClick}>
        <ManifestSearchBar />
        <ManifestGrid
          agGridTrackingProps={agGridTrackingProps}
          // Irrelevant for the fullscreen variant — isFullscreen already
          // forces infinite-scroll layout regardless of this value.
          isInfiniteScrollEnabled={false}
          // guest preview not supported; button is logged-in-only
          isLoggedIn
          listing={listing}
          setActiveSort={setActiveSort}
          setPage={setPage}
          variant="fullscreen"
        />
        <Fullscreen.Footer>
          <FooterRowCount
            columnDefs={columnDefs}
            end={end}
            sort={activeSort}
            start={start}
            total={total}
          />
        </Fullscreen.Footer>
      </Fullscreen.Body>
    </>
  )
}
